import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Redirect, useHistory, useParams } from "react-router";
import { useI18n } from "i18n";
import logger from "logging/logger";
import useRegisterToEvent from "modules/event/hooks/useRegisterToEvent";
import useBrowserInfo from "modules/common/hooks/useBrowserInfo";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import EventViewContainer from "modules/event/eventView/EventViewContainer";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import EventContextProvider from "modules/event/eventView/EventViewContext";
import { selectUser } from "modules/auth/redux/selectors";
import { ManageEventModal } from "modules/manageEvent";
import useEventDataHook from "modules/event/hooks/useEventDataHook";
import { isEventManager, isEventSpeaker } from "modules/event/utils/eventUtils";
import useEventDirectory from "modules/eventDirectory/hooks/useEventDirectory";
import { isJwtToken } from "helpers/jwtHelpers";
import WebviewWarning from "modules/compatibility/WebviewWarning";
import { IEvent } from "modules/event/types";
import { IEventMember } from "modules/eventMemberList/types";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { hasAutoJoin } from "modules/queryParams/queryParamsHelper";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { WhiteLabelSettingsLoader } from "modules/settings/components/WhiteLabelSettingsLoader";

/**
 * This component handles following use cases
 * Entry points
 *  - old invite - attendee-invite/token
 *  - new invite - /e/token
 *  - joined/my events pages
 *  - landing page
 */
interface EventContainerParams {
  token: string;
  eventCode: string;
  eventId: string;
  pageId: string;
}

const EventContainer = React.memo(() => {
  const history = useHistory();
  const { t } = useI18n(["common", "eventLanding"]);
  const user = useSelector(selectUser);
  const { registerToEvent } = useRegisterToEvent();
  const { getEventDataByCode, getEventDataByToken, getEventMemberData } =
    useEventDataHook();
  const {
    checkIfEventHappeningNow,
    checkIfEventEnded,
    checkIfEventIsUpcoming,
    checkIsAllowedToJoinEventLobby,
  } = useCheckEventStatus();
  const { manageEventDirectoryCache } = useEventDirectory();
  const { addWarningNotification, addInfoNotification } =
    useNotificationActions();
  const [isLoading, setIsLoading] = React.useState(true);
  const [eventMember, setEventMember] = React.useState<IEventMember | null>(
    null,
  );
  const { webViewInfo } = useBrowserInfo();

  const [event, setEvent] = React.useState<IEvent | null>(null);
  const isSpeaker = isEventSpeaker(event ?? undefined, user?.id, user?.email);
  const isManager = isEventManager(event ?? undefined, user?.id);
  const queryParams = useQueryParams();
  const isAutoJoin = hasAutoJoin(queryParams);

  const isOngoingEvent = useMemo(
    () => event && checkIfEventHappeningNow(event),
    // these helper fns should not be included in the dependency array
    // because it is not memoized, to prevent re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event],
  );

  const isPastEvent = useMemo(
    () => event && checkIfEventEnded(event),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event],
  );

  const isUpcomingEvent = useMemo(
    () => event && checkIfEventIsUpcoming(event),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event],
  );

  const shouldAllowEventMember =
    eventMember &&
    (isOngoingEvent ||
      (isUpcomingEvent &&
        event &&
        Boolean(event.isLobbyActive) &&
        checkIsAllowedToJoinEventLobby(event)));

  const isLoginAndHasEvent = !!user && !!event;
  const isAllowAutoJoin =
    isLoginAndHasEvent &&
    (shouldAllowEventMember || (!isPastEvent && isSpeaker) || isManager);

  const params = useParams<EventContainerParams>();
  const { token } = params;
  let { eventCode } = params;

  // Check if event code or token - event code will have extra digits for duplicate event names
  if (!isJwtToken(token)) {
    eventCode = token;
  }

  const onMount = async () => {
    let eventData;

    setIsLoading(true);

    // Get event data either by event code or jwt token
    if (eventCode) {
      eventData = await getEventDataByCode(eventCode);
    } else if (token) {
      eventData = await getEventDataByToken(token);
    }

    // If event data is available, get event member
    if (eventData && eventData._id) {
      try {
        const eventDirectories: string[] = [];
        if (eventData?.directoryId) {
          eventDirectories.push(eventData.directoryId as string);
        }
        if (eventData?.directories) {
          eventDirectories.push(...(eventData.directories as string[]));
        }

        manageEventDirectoryCache(eventDirectories);

        // auto join event
        if (isAutoJoin && !eventData.eventcubeEventId && !eventData.isPrivate) {
          await registerToEvent(eventData.id);
        }

        const eventMemberData = await getEventMemberData(eventData.id);

        if (eventMemberData) {
          if (eventMemberData.isBlocked) {
            addInfoNotification({
              message: t("eventLanding:removed.event.message"),
            });
            history.push(`/e/${eventData.code}/register`);
          }
          setEventMember(eventMemberData);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "";

        logger.error(
          `[EventWrapperContainer] Error getting event member for event: ${eventData._id} msg: ${errorMessage}`,
        );
      }
      setEvent(eventData);
    } else {
      addWarningNotification({
        message: t("eventLanding:event.notfound"),
      });
      history.push("/404");
    }

    setIsLoading(false);
  };

  React.useEffect(() => {
    onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventCode]);

  // Show loading page
  if (isLoading) {
    return (
      <PageLoader message={t("wait") as string} data-testid="page-loading" />
    );
  }

  // check if user has direct access
  if (isAllowAutoJoin && event?.code && event?._id) {
    if (webViewInfo.app != null) {
      return <WebviewWarning app={webViewInfo.app} />;
    }
    return (
      <>
        <EventContextProvider eventCode={event.code}>
          <EventViewContainer event={event} />
        </EventContextProvider>
        {params && event && <ManageEventModal {...params} />}
        <WhiteLabelSettingsLoader />
      </>
    );
  }

  return (
    <Redirect to={`/e/${event?.code}/register${window.location.search}`} />
  );
});

export default EventContainer;
